import * as logClient from "@classdojo/log-client";
import { HeadlineText, LabelText, Space, theme } from "@classdojo/web/nessie";
import { keyframes, RAW_cssValue } from "@classdojo/web/nessie/stylingLib";
import { useTranslation } from "@web-monorepo/i18next";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import ToolkitAppScroller from "components/BeyondToolkit/ToolkitAppScroller";
import { IdeasPauseIcon, IdeasPlayIcon } from "components/IdeasIcons";
import RoundedIconButton from "components/RoundedIconButton";
import ToolkitCard from "components/ToolkitCard";
import once from "lodash/once";
import { useMemo, useRef, useState } from "react";
import Lottie from "react-lottie";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { MUSIC } from "utils/beyondToolkitTools";
import { sendUsage } from "utils/freemium";
import animationData from "./note_wiggle.json";

function Music() {
  // this tells i18next to load these namespaces
  const { ready } = useTranslation(["dojo.beyond"]);

  const [channelSelected, setChannelSelected] = useState<Channel | null>(null);
  const [trackNumber, setTrackNumber] = useState(0);
  const [paused, setPaused] = useState(false);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const { title, color, imgSrc } = MUSIC();

  const audioNode = useRef<HTMLAudioElement | null>(null);

  const openChannel = (index: number) => {
    const channelList = channels(true);
    logParentToolkitEvent({
      eventName: `music.${channelList[index].loggingId}.tap`,
    });
    setChannelSelected(channelList[index]);
    setPaused(false);
  };

  const nextTrack = () => {
    if (!channelSelected) {
      return;
    }

    const currentTrackNumber = trackNumber;
    setTrackNumber((currentTrackNumber + 1) % channelSelected.tracks.length);
    setPaused(false);
  };

  const previousTrack = () => {
    if (!channelSelected) {
      return;
    }

    const currentTrackNumber = trackNumber;
    setTrackNumber((channelSelected.tracks.length + (currentTrackNumber - 1)) % channelSelected.tracks.length);
    setPaused(false);
  };

  const togglePause = () => {
    if (paused) {
      audioNode.current?.play().catch(() => {
        logClient.sendMetrics([
          {
            type: "increment",
            value: 1,
            metricName: "toolkit.media.audio.play.error",
          },
        ]);
      });
    } else {
      audioNode.current?.pause();
    }

    setPaused(!paused);
  };

  const _sendUsage = useMemo(
    () =>
      once(() => {
        sendUsage("sleepMusic");
      }),
    [],
  );

  if (!ready) return null;

  return (
    <ToolkitAppScroller title={title} color={color} imgSrc={imgSrc}>
      {channelSelected == null ? (
        <div
          sx={{
            display: "grid",
            gridGap: "dt_xs",
            margin: "dt_s",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          {channels(true).map((channel, i) => {
            return (
              <div
                key={i}
                onClick={() => openChannel(i)}
                sx={{
                  textAlign: "center",
                  flex: 1,
                  padding: "dt_m",
                  margin: "dt_xs",
                  border: "dt_card",
                  borderRadius: "dt_radius_s",
                  position: "relative",
                }}
              >
                <img width="108px" src={channel.imgSrc}></img>
                <Space size="s" />
                <LabelText>{channel.title}</LabelText>
              </div>
            );
          })}
        </div>
      ) : (
        <ToolkitCard close={() => setChannelSelected(null)}>
          <HeadlineText as="h2">{channelSelected.title}</HeadlineText>
          <Space size="m" />
          <div sx={{ margin: "dt_s", position: "relative" }}>
            <div sx={{ position: "relative" }}>
              <Lottie
                // eslint-disable-next-line @web-monorepo/no-style-prop
                style={{ marginTop: "-100px" }}
                options={defaultOptions}
                height={250}
                width={450}
                isStopped={false}
                isPaused={paused}
              />
              <img
                src={"/static/images/radio.png"}
                sx={{
                  marginTop: RAW_cssValue("-200px"),
                  width: 200,
                  height: 135,
                  animation: `${radioAnimationKeyframes} infinite ${channelSelected.animationSpeed || "2s"} linear;`,
                  animationPlayState: `${paused ? "paused" : "running"}`,
                  transformOrigin: "bottom",
                }}
              />
            </div>
          </div>
          <Space size="m" />
          <audio
            src={channelSelected.tracks[trackNumber].src}
            autoPlay={true}
            ref={audioNode}
            onPlay={_sendUsage}
            onEnded={nextTrack}
          />
          <div
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            <img onClick={() => previousTrack()} width="25px" src="/static/images/previous_icon.png"></img>
            <div
              sx={{
                marginRight: "dt_m",
                marginLeft: "dt_m",
                display: "flex",
                position: "relative",
              }}
            >
              <RoundedIconButton
                backgroundColor={theme.colors.dt_content_accent}
                onClick={() => togglePause()}
                data-name="beyond_toolkit_music:play_pause:button"
              >
                {paused ? (
                  <IdeasPlayIcon size="l" color="dt_content_light" />
                ) : (
                  <IdeasPauseIcon size="l" color="dt_content_light" />
                )}
              </RoundedIconButton>
            </div>
            <img
              onClick={() => nextTrack()}
              width="25px"
              src="/static/images/previous_icon.png"
              sx={{ transform: "scaleX(-1)" }}
            ></img>
          </div>
        </ToolkitCard>
      )}
    </ToolkitAppScroller>
  );
}

export default Music;

type Channel = {
  title: string;
  imgSrc: string;
  tracks: {
    src: string;
  }[];
  animationSpeed: string;
  loggingId: string;
};

const channels = (
  isSleepMusicVariation = true, //currently defaulted to only bedtime music channels
): Channel[] =>
  [
    {
      title: autoTranslate("Focus"),
      imgSrc: "/static/images/radio_focus.png",
      tracks: Array.from({ length: 5 }, (_, n) => ({
        src: `https://static.classdojo.com/toolkit-static/radio/calm_${n + 1}.mp3`, //focus tracks are named 'calm' ¯\_(ツ)_/¯
      })),
      animationSpeed: "2s",
      loggingId: "focus",
    },
    {
      title: autoTranslate("Active"),
      imgSrc: "/static/images/radio_active.png",
      tracks: Array.from({ length: 5 }, (_, n) => ({
        src: `https://static.classdojo.com/toolkit-static/radio/upbeat_${n + 1}.mp3`,
      })),
      animationSpeed: "1s",
      loggingId: "active",
    },
    {
      title: autoTranslate("Calm"),
      imgSrc: "/static/images/radio_calm.png",
      tracks: Array.from({ length: 5 }, (_, n) => ({
        src: `https://static.classdojo.com/toolkit-static/radio/beyond_calm_${n + 1}.mp3`,
      })),
      animationSpeed: "3s",
      loggingId: "calm",
    },
    {
      title: autoTranslate("Sleep"),
      imgSrc: "/static/images/radio_sleep.png",
      tracks: Array.from({ length: 5 }, (_, n) => ({
        src: `https://static.classdojo.com/toolkit-static/radio/beyond_sleep_${n + 1}.mp3`,
      })),
      animationSpeed: "3s",
      loggingId: "sleep",
    },
  ].slice(isSleepMusicVariation ? 2 : 0, 4);

const radioAnimationKeyframes = keyframes`
  0%   {transform: scaleX(1) scaleY(1);}
  25%  {transform: scaleX(1.25) scaleY(1);}
  50%  {transform: scaleX(1) scaleY(1);}
  75% {transform: scaleX(1) scaleY(1.25);}
  100% {transform: scaleX(1) scaleY(1);}
`;
